/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useMemo, useRef, useState } from "react";
import Chart from "react-apexcharts";
import { PrimaryButton } from "../buttons/PrimaryButton";
import axios from "axios";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
// @ts-ignore
import { useScreenshot } from "use-react-screenshot";
import { useDropStats } from "../../store/useDropStats";
import { useDropLocations } from "../../store/useDropLocations";
import {
  sortLocationNames,
  getOrderedLabelsFromFilterPlaces,
} from "../../utils/locationOrdering";
import {
  Menu,
  MenuHandler,
  MenuItem,
  MenuList,
  Tooltip,
} from "@material-tailwind/react";
import { ReactComponent as ChevronDown } from "../../assets/icons/chevrons/chevron-down.svg";
import InformationIcon from "../../assets/icons/info/information.png";
import {
  getMaterialColor,
  getMaterialContrastColor,
} from "../../constants/plasticColors";

dayjs.extend(isoWeek);

interface MaterialsRevenueProps {
  filterType?: "location" | "city";
  filterPlaces?: string[];
  sequenceStep?: number;
  setSequenceStep?: (n: number) => void;
  sequenceStepIndex?: number;
}

export const MaterialsRevenue = React.memo(
  ({
    filterType,
    filterPlaces,
    sequenceStep,
    setSequenceStep,
    sequenceStepIndex,
  }: MaterialsRevenueProps) => {
    const useGeneralFilter =
      filterType !== undefined && filterPlaces !== undefined;
    const snapshotRef = useRef(null);
    const {
      materialsRevenuePerMaterialPerLocation,
      getMaterialsRevenuePerMaterialPerLocation,
      isMaterialsRevenuePerMaterialLoading,
      isMaterialsRevenuePerMaterialError,
    } = useDropStats();
    const { dropLocations, getDropLocations } = useDropLocations();

    const TOTAL_ALL_STORES_ID = "__TOTAL_ALL_STORES__";

    const [_, takeScreenshot] = useScreenshot();
    const [isCsvDownloading, setIsCsvDownloading] = useState(false);
    const init12Months = () => {
      const end = dayjs();
      const start = end.subtract(12, "month");
      return { start, end };
    };
    const [period, setPeriod] = useState<"month" | "week" | "quarter" | "year">(
      "month"
    );
    const [startYear, setStartYear] = useState(() =>
      init12Months().start.year()
    );
    const [startMonth, setStartMonth] = useState(
      () => init12Months().start.month() + 1
    );
    const [startWeek, setStartWeek] = useState(() =>
      init12Months().start.isoWeek()
    );
    const [startQuarter, setStartQuarter] = useState(() =>
      Math.ceil((init12Months().start.month() + 1) / 3)
    );
    const [endYear, setEndYear] = useState(() => init12Months().end.year());
    const [endMonth, setEndMonth] = useState(
      () => init12Months().end.month() + 1
    );
    const [endWeek, setEndWeek] = useState(() => init12Months().end.isoWeek());
    const [endQuarter, setEndQuarter] = useState(() =>
      Math.ceil((init12Months().end.month() + 1) / 3)
    );

    const yearOptions = Array.from(
      { length: dayjs().year() - 2023 },
      (_, i) => dayjs().year() - i
    );

    const monthOptions = [
      { value: 1, label: "Jan" },
      { value: 2, label: "Feb" },
      { value: 3, label: "Mar" },
      { value: 4, label: "Apr" },
      { value: 5, label: "May" },
      { value: 6, label: "Jun" },
      { value: 7, label: "Jul" },
      { value: 8, label: "Aug" },
      { value: 9, label: "Sep" },
      { value: 10, label: "Oct" },
      { value: 11, label: "Nov" },
      { value: 12, label: "Dec" },
    ];

    const quarterOptions = [
      { value: 1, label: "Q1" },
      { value: 2, label: "Q2" },
      { value: 3, label: "Q3" },
      { value: 4, label: "Q4" },
    ];

    const getWeekOptions = (year: number) => {
      const lastDay = dayjs(`${year}-12-31`);
      const totalWeeks = lastDay.isoWeek() === 1 ? 52 : lastDay.isoWeek();
      const currentWeek = dayjs().isoWeek();
      const currentYear = dayjs().year();
      return Array.from({ length: totalWeeks }, (_, i) => {
        const w = i + 1;
        const start = dayjs().year(year).isoWeek(w).startOf("isoWeek");
        const end = dayjs().year(year).isoWeek(w).endOf("isoWeek");
        return {
          value: w,
          label: `Week ${w}: ${start.format("MMM DD")} - ${end.format("MMM DD")}`,
          disabled: year === currentYear && w > currentWeek,
        };
      });
    };

    const startMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return startYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [startYear]);

    const endMonthOptions = useMemo(() => {
      const cy = dayjs().year();
      const cm = dayjs().month() + 1;
      return endYear === cy
        ? monthOptions.filter((m) => m.value <= cm)
        : monthOptions;
    }, [endYear]);

    const startWeekOptions = useMemo(
      () => getWeekOptions(startYear),
      [startYear]
    );
    const endWeekOptions = useMemo(() => getWeekOptions(endYear), [endYear]);

    const downloadScreenshot = () => {
      takeScreenshot(snapshotRef.current).then((image: any) => {
        const link = document.createElement("a");
        link.href = image;
        link.download = "materials_revenue.png";
        link.click();
      });
    };

    const downloadCsv = () => {
      setIsCsvDownloading(true);
      const params: Record<string, string> = {
        fromYear: startYear.toString(),
        toYear: endYear.toString(),
      };
      if (period === "month") {
        params.fromMonth = startMonth.toString();
        params.toMonth = endMonth.toString();
      } else if (period === "quarter") {
        params.fromQuarter = startQuarter.toString();
        params.toQuarter = endQuarter.toString();
      } else if (period === "week") {
        params.fromWeek = startWeek.toString();
        params.toWeek = endWeek.toString();
      } else {
        params.fromMonth = "1";
        params.toMonth = "12";
      }
      if (useGeneralFilter) {
        params.type = filterType ?? "location";
        const realPlaces =
          (filterPlaces ?? []).filter((p) => p !== TOTAL_ALL_STORES_ID) ?? [];
        if (realPlaces.length > 0) {
          params.places = realPlaces.join(",");
        }
      }

      axios
        .get("/drops/stats/materials-revenue-per-material-per-location/csv", {
          params,
          responseType: "blob",
        })
        .then((response) => {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          const contentDisposition = response.headers["content-disposition"];
          let filename = "materials_revenue_daily.csv";
          if (contentDisposition) {
            const filenameMatch = contentDisposition.match(/filename="(.+)"/);
            if (filenameMatch) {
              filename = filenameMatch[1];
            }
          }
          link.setAttribute("download", filename);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        })
        .catch((error) => {
          console.error("Error downloading CSV:", error);
        })
        .finally(() => {
          setIsCsvDownloading(false);
        });
    };

    // Materials as stacks, locations on x-axis (like Materials chart)
    const { chartData, categoryLabels } = useMemo(() => {
      const raw = materialsRevenuePerMaterialPerLocation ?? [];
      if (!raw.length) return { chartData: [], categoryLabels: [] };

      const allLocations = new Set<string>();
      raw.forEach((material) => {
        material.data?.forEach(([loc]) => allLocations.add(loc));
      });
      const realFilterPlaces = (filterPlaces ?? []).filter(
        (p) => p !== TOTAL_ALL_STORES_ID
      );

      let orderedLocations: string[];
      if (useGeneralFilter && realFilterPlaces.length > 0) {
        const userOrderedLabels = getOrderedLabelsFromFilterPlaces(
          filterPlaces ?? [],
          filterType ?? "location",
          dropLocations,
          TOTAL_ALL_STORES_ID
        );
        const apiSet = new Set(allLocations);
        orderedLocations = userOrderedLabels.filter((l) => apiSet.has(l));
      } else {
        orderedLocations = sortLocationNames(
          Array.from(allLocations),
          dropLocations
        );
      }

      const includeTotalAllStores =
        useGeneralFilter && (filterPlaces ?? []).includes(TOTAL_ALL_STORES_ID);
      const totalLabel = "Total all stores";
      const orderedLocationsWithTotal = includeTotalAllStores
        ? [...orderedLocations, totalLabel]
        : orderedLocations;

      const transformedData = raw.map((material) => {
        const totalForMaterial =
          material.data?.reduce(
            (sum, [, val]) => sum + (typeof val === "number" ? val : 0),
            0
          ) ?? 0;

        const data = orderedLocationsWithTotal.map((location) => {
          if (includeTotalAllStores && location === totalLabel) {
            return totalForMaterial;
          }
          const entry = material.data?.find(
            ([loc]: [string, number]) => loc === location
          );
          return entry ? entry[1] : 0;
        });

        return {
          name: material.name,
          data,
        };
      });

      const filteredData = transformedData.filter((m) =>
        m.data.some((v) => v > 0)
      );
      const reversedData = [...filteredData].reverse();

      return {
        chartData: reversedData,
        categoryLabels: orderedLocationsWithTotal,
      };
    }, [
      materialsRevenuePerMaterialPerLocation,
      filterPlaces,
      filterType,
      useGeneralFilter,
      dropLocations,
    ]);

    useEffect(() => {
      getDropLocations();
    }, []);

    const prevFilterSigRef = useRef<string | null>(null);

    useEffect(() => {
      if (
        sequenceStep !== undefined &&
        setSequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep < sequenceStepIndex
      ) {
        return;
      }
      const filterSig = JSON.stringify({
        period,
        startYear,
        startMonth,
        startWeek,
        startQuarter,
        endYear,
        endMonth,
        endWeek,
        endQuarter,
        useGeneralFilter,
        filterType,
      });
      if (
        sequenceStep !== undefined &&
        sequenceStepIndex !== undefined &&
        sequenceStep > sequenceStepIndex &&
        prevFilterSigRef.current === filterSig
      ) {
        return;
      }
      prevFilterSigRef.current = filterSig;

      const apiPeriod: "month" | "week" | "quarter" =
        period === "year" ? "month" : period;

      const fromToParams = {
        fromYear: startYear,
        toYear: endYear,
        fromMonth:
          apiPeriod === "month"
            ? period === "year"
              ? 1
              : startMonth
            : undefined,
        toMonth:
          apiPeriod === "month"
            ? period === "year"
              ? 12
              : endMonth
            : undefined,
        fromWeek: apiPeriod === "week" ? startWeek : undefined,
        toWeek: apiPeriod === "week" ? endWeek : undefined,
        fromQuarter: apiPeriod === "quarter" ? startQuarter : undefined,
        toQuarter: apiPeriod === "quarter" ? endQuarter : undefined,
      };
      const promise = getMaterialsRevenuePerMaterialPerLocation(
        apiPeriod,
        fromToParams,
        useGeneralFilter ? (filterType ?? "location") : undefined,
        [] // Fetch all; frontend filters by filterPlaces (like Materials chart)
      );
      if (
        setSequenceStep != null &&
        sequenceStepIndex != null &&
        sequenceStep === sequenceStepIndex
      ) {
        Promise.resolve(promise).finally(() =>
          setSequenceStep(sequenceStepIndex + 1)
        );
      }
    }, [
      period,
      startYear,
      startMonth,
      startWeek,
      startQuarter,
      endYear,
      endMonth,
      endWeek,
      endQuarter,
      useGeneralFilter,
      filterType,
      sequenceStep,
      setSequenceStep,
      sequenceStepIndex,
    ]);

    return (
      <div className="flex flex-col w-full p-2 sm:p-7 bg-white rounded-xl shadow-none mb-1 sm:mb-10">
        <div className="flex flex-col sm:flex-row w-full items-start justify-between">
          <div></div>
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-center justify-start sm:justify-end gap-2 sm:gap-3">
            <div className="flex items-center gap-1 sm:gap-2">
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm whitespace-nowrap">
                    {period === "month"
                      ? "Monthly"
                      : period === "week"
                        ? "Weekly"
                        : period === "quarter"
                          ? "Quarterly"
                          : "Yearly"}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  <MenuItem
                    disabled={period === "month"}
                    onClick={() => setPeriod("month")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Monthly
                  </MenuItem>
                  <MenuItem
                    disabled={period === "week"}
                    onClick={() => setPeriod("week")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Weekly
                  </MenuItem>
                  <MenuItem
                    disabled={period === "quarter"}
                    onClick={() => setPeriod("quarter")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Quarterly
                  </MenuItem>
                  <MenuItem
                    disabled={period === "year"}
                    onClick={() => setPeriod("year")}
                    className="font-satoshi text-base text-dark"
                    placeholder={undefined}
                  >
                    Yearly
                  </MenuItem>
                </MenuList>
              </Menu>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {startYear}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {yearOptions.map((y) => (
                    <MenuItem
                      key={y}
                      disabled={startYear === y}
                      onClick={() => setStartYear(y)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              {period === "month" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {startMonthOptions.find((m) => m.value === startMonth)
                        ?.label || "Month"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {startMonthOptions.map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={startMonth === m.value}
                        onClick={() => setStartMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "week" && (
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        startWeekOptions.find((w) => w.value === startWeek)
                          ?.label
                      }
                    >
                      {startWeekOptions.find((w) => w.value === startWeek)
                        ?.label || "Week"}
                      <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {startWeekOptions.map((w) => (
                      <MenuItem
                        key={w.value}
                        disabled={w.disabled || startWeek === w.value}
                        onClick={() => setStartWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "quarter" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {quarterOptions.find((q) => q.value === startQuarter)
                        ?.label || "Q1"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {quarterOptions.map((q) => (
                      <MenuItem
                        key={q.value}
                        disabled={startQuarter === q.value}
                        onClick={() => setStartQuarter(q.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {q.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              <label className="font-satoshi text-xs sm:text-sm text-gray-text whitespace-nowrap">
                To:
              </label>
              <Menu>
                <MenuHandler>
                  <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                    {endYear}
                    <ChevronDown className="ml-1 sm:ml-2" />
                  </button>
                </MenuHandler>
                <MenuList placeholder={undefined}>
                  {yearOptions.map((y) => (
                    <MenuItem
                      key={y}
                      disabled={endYear === y}
                      onClick={() => setEndYear(y)}
                      className="font-satoshi text-base text-dark"
                      placeholder={undefined}
                    >
                      {y}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>
              {period === "month" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {endMonthOptions.find((m) => m.value === endMonth)
                        ?.label || "Month"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {endMonthOptions.map((m) => (
                      <MenuItem
                        key={m.value}
                        disabled={endMonth === m.value}
                        onClick={() => setEndMonth(m.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {m.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "week" && (
                <Menu>
                  <MenuHandler>
                    <button
                      className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm max-w-[180px] sm:max-w-[220px] whitespace-nowrap overflow-hidden text-ellipsis"
                      title={
                        endWeekOptions.find((w) => w.value === endWeek)?.label
                      }
                    >
                      {endWeekOptions.find((w) => w.value === endWeek)?.label ||
                        "Week"}
                      <ChevronDown className="ml-1 sm:ml-2 flex-shrink-0" />
                    </button>
                  </MenuHandler>
                  <MenuList
                    className="rounded-none max-h-[250px] overflow-y-auto"
                    placeholder={undefined}
                  >
                    {endWeekOptions.map((w) => (
                      <MenuItem
                        key={w.value}
                        disabled={w.disabled || endWeek === w.value}
                        onClick={() => setEndWeek(w.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {w.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
              {period === "quarter" && (
                <Menu>
                  <MenuHandler>
                    <button className="h-8 px-2 sm:px-3 border rounded border-gray-border bg-white flex items-center justify-center font-satoshi font-medium text-gray-text text-xs sm:text-sm">
                      {quarterOptions.find((q) => q.value === endQuarter)
                        ?.label || "Q4"}
                      <ChevronDown className="ml-1 sm:ml-2" />
                    </button>
                  </MenuHandler>
                  <MenuList placeholder={undefined}>
                    {quarterOptions.map((q) => (
                      <MenuItem
                        key={q.value}
                        disabled={endQuarter === q.value}
                        onClick={() => setEndQuarter(q.value)}
                        className="font-satoshi text-base text-dark"
                        placeholder={undefined}
                      >
                        {q.label}
                      </MenuItem>
                    ))}
                  </MenuList>
                </Menu>
              )}
            </div>

            {!isMaterialsRevenuePerMaterialLoading &&
              materialsRevenuePerMaterialPerLocation !== null && (
                <>
                  <PrimaryButton
                    size="sm"
                    onClick={downloadCsv}
                    label={isCsvDownloading ? "Downloading..." : "Download CSV"}
                    disabled={isCsvDownloading || chartData.length === 0}
                    className="!bg-droppie-oranje !border-droppie-oranje"
                  />
                  <PrimaryButton
                    size="sm"
                    onClick={downloadScreenshot}
                    label="Download image"
                    className="!bg-droppie-oranje !border-droppie-oranje"
                  />
                </>
              )}
          </div>
        </div>

        <section ref={snapshotRef} className="mt-4">
          <div className="flex items-center mb-2 sm:mb-12">
            <p className="font-satoshi font-bold text-lg sm:text-2xl text-tailgrids-black">
              Materials Revenue (€)
              <Tooltip content="Revenue = kg or items × price per kg or item (Droppie receives)">
                <img
                  src={InformationIcon}
                  alt="info"
                  className="inline-block w-4 h-4 ml-2 cursor-pointer"
                />
              </Tooltip>
            </p>
          </div>
          <div>
            {isMaterialsRevenuePerMaterialLoading ? (
              <div className="flex items-center justify-center h-[300px] w-full">
                <svg
                  aria-hidden="true"
                  className="w-8 h-8 text-gray-200 animate-spin fill-primary-base"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                  <path
                    d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                    fill="currentFill"
                  />
                </svg>
              </div>
            ) : isMaterialsRevenuePerMaterialError ? (
              <div className="flex items-center justify-center h-[300px] w-full">
                <p className="text-red-500 font-satoshi">
                  Failed to load data. Please try again.
                </p>
              </div>
            ) : chartData.length > 0 && categoryLabels.length > 0 ? (
              <Chart
                type="bar"
                options={{
                  ...options,
                  colors:
                    chartData.length > 0
                      ? chartData.map((s) => {
                          const baseColor = getMaterialColor(s.name);
                          const contrastColor = getMaterialContrastColor(
                            s.name
                          );
                          return function ({
                            dataPointIndex,
                            w,
                          }: {
                            dataPointIndex: number;
                            w: any;
                          }) {
                            const labels = w?.globals?.labels ?? [];
                            const isTotalBar =
                              labels[dataPointIndex] === "Total all stores";
                            return isTotalBar ? contrastColor : baseColor;
                          };
                        })
                      : ["#e34e76"],
                  xaxis: {
                    categories: categoryLabels,
                    title: {
                      text:
                        (filterType ?? "location") === "city"
                          ? "City"
                          : "Store",
                      style: { fontSize: "14px", fontWeight: "bold" },
                    },
                    labels: {
                      style: {
                        fontSize: "12px",
                        fontFamily: "Satoshi, sans-serif",
                        colors: categoryLabels.map((l) =>
                          l === "Total all stores" ? "#c2410c" : "#374151"
                        ),
                      },
                      formatter: (val: string) =>
                        val === "Total all stores" ? `${val} (sum)` : val,
                    },
                  },
                  tooltip: {
                    ...options.tooltip,
                    custom: function ({
                      series,
                      seriesIndex,
                      dataPointIndex,
                      w,
                    }: any) {
                      const resolveColor = (idx: number) => {
                        const c = w.config.colors?.[idx];
                        if (typeof c === "function") {
                          return c({ dataPointIndex, w, seriesIndex: idx });
                        }
                        return c || "#808080";
                      };
                      const seriesData = series
                        .map((sv: any, i: number) => ({
                          name: w.globals.seriesNames[i],
                          value: sv[dataPointIndex],
                          color: resolveColor(i),
                        }))
                        .filter((item: any) => Number(item.value) > 0)
                        .sort((a: any, b: any) => b.value - a.value);
                      const categories =
                        w.globals.labels?.[dataPointIndex] ?? "";
                      let tooltipHtml =
                        '<div class="apexcharts-tooltip-title" style="font-family: Satoshi, sans-serif; font-size: 12px;">';
                      tooltipHtml += `${categories}</div>`;
                      seriesData.forEach((item: any) => {
                        const formatted = new Intl.NumberFormat("nl-NL", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(Number(item.value ?? 0) / 100);
                        tooltipHtml += `
                        <div class="apexcharts-tooltip-series-group apexcharts-active" style="order: 1; display: flex;">
                          <span class="apexcharts-tooltip-marker" style="background-color: ${item.color};"></span>
                          <div class="apexcharts-tooltip-text" style="font-family: Satoshi, sans-serif; font-size: 12px;">
                            <div class="apexcharts-tooltip-y-group">
                              <span class="apexcharts-tooltip-text-y-label">${item.name}: </span>
                              <span class="apexcharts-tooltip-text-y-value">${formatted}</span>
                            </div>
                          </div>
                        </div>
                      `;
                      });
                      return tooltipHtml;
                    },
                  },
                }}
                series={chartData}
                height={450}
              />
            ) : (
              <div className="flex items-center justify-center h-[300px] w-full">
                <p className="text-gray-500 font-satoshi">
                  No data available for the selected filters.
                </p>
              </div>
            )}
          </div>
        </section>
      </div>
    );
  }
);

const options = {
  chart: {
    type: "bar" as const,
    stacked: true,
    zoom: { enabled: false },
    toolbar: { show: false },
  },
  stroke: {
    show: true,
    width: 2,
    colors: ["#fff"],
  },
  plotOptions: {
    bar: {
      horizontal: false,
      columnWidth: "55%",
      borderRadius: 4,
    },
  },
  dataLabels: {
    enabled: true,
    style: {
      fontSize: "10px",
      fontFamily: "Satoshi, sans-serif",
      colors: ["#FFFFFF"],
      fontWeight: 500,
    },
    formatter: function (value: number) {
      if (value === 0) return "";
      return new Intl.NumberFormat("nl-NL", {
        style: "currency",
        currency: "EUR",
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }).format(Number(value ?? 0) / 100);
    },
  },
  grid: { strokeDashArray: 5 },
  legend: {
    show: true,
    position: "right" as const,
    offsetY: 0,
    height: 300,
    fontSize: "14px",
    fontFamily: "Satoshi, sans-serif",
    labels: { colors: "#374151" },
    markers: {
      width: 12,
      height: 12,
      strokeWidth: 0,
      radius: 6,
    },
    itemMargin: { horizontal: 0, vertical: 4 },
  },
  tooltip: {
    shared: true,
    intersect: false,
  },
  yaxis: {
    title: {
      text: "Materials (€)",
      style: { fontSize: "14px", fontWeight: "bold" },
    },
    labels: {
      formatter: function (value: number) {
        return new Intl.NumberFormat("nl-NL", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(Number(value ?? 0) / 100);
      },
    },
  },
  colors: [
    "#e34e76",
    "#007887",
    "#8773aa",
    "#e6e1ff",
    "#fe3e3e",
    "#ffff78",
    "#d2e6ff",
    "#d2d2b2",
    "#5fd0ae",
    "#ff8dfa",
    "#ff6e41",
    "#808080",
  ],
};
